import React, { useContext, useState } from "react";
import {
  GridContainer,
  InvoicesListRootArea,
  GridItem,
} from "./Invoices.styles";
import { Box, Typography, Button, IconButton, Chip, TextField } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DataTable from "../../dataTable/DataTable";
import { useEffectOnce } from "../../../utils/effects";
import { useDispatch, useSelector } from "react-redux";
import { getInvoicesList, deleteInvoice } from "./Invoices.thunk";
import {
  setInvoiceId,
  ResetStore,
} from "../../forms/Invoices/InvoiceForm.slice";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AuthContext from "../../../context/AuthContext";
import { useConfirmModal } from "../../confirmModal/useConfirmModal";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { formatCurrencyAmount } from "../../../utils/currencyFormat";
import { formatUtcToLocal, getLocalTzLabel } from "../../../services/dateUtils";
import { formatExpiresAtForDisplay } from "../../forms/Invoices/InvoiceForm.resources";
import {
  formatKycStatusChip,
  formatPaymentStatusChip,
} from "../KycStatusList/kycStatusUtils";

const tzLabel = getLocalTzLabel();
const withTz = (label) => (tzLabel ? `${label} (${tzLabel})` : label);

const Invoices = (props) => {
  const { setSubmenuMerchantTerminal, setSelectedOption, setNameSection } =
    props;

  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const { showConfirmModal } = useConfirmModal();

  const {
    invoicesList,
    status,
    page,
    pageSize,
    totalRecords,
    client_info_id,
  } = useSelector((state) => state.invoices);

  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const buildListPayload = (overrides = {}) => {
    const payload = {
      token,
      page: overrides.page ?? page,
      page_size: overrides.page_size ?? pageSize,
      client_info_id,
    };
    const search =
      overrides.search !== undefined ? overrides.search : appliedSearch;
    if (search) {
      payload.search = search;
    }
    return payload;
  };

  const loadList = (overrides = {}) => {
    if (!client_info_id) return;
    dispatch(getInvoicesList(buildListPayload(overrides)));
  };

  useEffectOnce(() => {
    loadList();
  });

  const handleGoBack = () => {
    setSubmenuMerchantTerminal("billing_info");
    setSelectedOption("billing_info");
    setNameSection("Merchant & Terminal - Customer Info");
  };

  const handleAdd = () => {
    dispatch(ResetStore());
    dispatch(setInvoiceId(null));
    setSubmenuMerchantTerminal("invoice_form");
    setSelectedOption("invoice_form");
    setNameSection("Merchant & Terminal - Create Invoice");
  };

  const handleEdit = (row) => {
    dispatch(ResetStore());
    dispatch(setInvoiceId(row.id));
    setSubmenuMerchantTerminal("invoice_form");
    setSelectedOption("invoice_form");
    setNameSection("Merchant & Terminal - Edit Invoice");
  };

  const handleDelete = (row) => {
    const invoiceNumber = row.invoice_number || `Invoice #${row.id}`;
    showConfirmModal({
      message: `Are you sure you want to cancel invoice ${invoiceNumber}? This action cannot be undone.`,
      actionId: `delete-invoice-${row.id}`,
      title: "Cancel Invoice",
      onConfirm: () => {
        dispatch(
          deleteInvoice({
            token,
            invoiceId: row.id,
            page,
            page_size: pageSize,
            client_info_id,
            search: appliedSearch || undefined,
          })
        );
      },
    });
  };

  const handlePageChange = (event, newPage) => {
    loadList({ page: newPage + 1 });
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = +event.target.value;
    loadList({ page: 1, page_size: newPageSize });
  };

  const handleSearch = () => {
    const nextSearch = searchQuery.trim();
    setAppliedSearch(nextSearch);
    loadList({ page: 1, search: nextSearch || undefined });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setAppliedSearch("");
    loadList({ page: 1, search: undefined });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const columns = [
    { id: "invoice_number", label: "Invoice Number", minWidth: 150 },
    {
      id: "amount",
      label: "Amount",
      minWidth: 120,
      align: "right",
      format: (value, row) =>
        formatCurrencyAmount(value, row?.currency, { emptyDisplay: "-" }),
    },
    { id: "currency", label: "Currency", minWidth: 80 },
    {
      id: "expires_at",
      label: "Expiration Date",
      minWidth: 130,
      format: (value) => formatExpiresAtForDisplay(value),
    },
    {
      id: "status",
      label: "Payment Status",
      minWidth: 120,
      format: (value) => {
        const chip = formatPaymentStatusChip(value);
        if (!chip) return "-";
        return (
          <Chip size="small" label={chip.label} sx={chip.sx} />
        );
      },
    },
    {
      id: "kyc_status",
      label: "KYC Status",
      minWidth: 110,
      format: (value) => {
        const chip = formatKycStatusChip(value);
        if (!chip) return "-";
        return (
          <Chip size="small" label={chip.label} sx={chip.sx} />
        );
      },
    },
    {
      id: "invoice_url",
      label: "Invoice URL",
      minWidth: 150,
      format: (value, row) => {
        if (!value) return "-";
        return (
          <Button
            variant="outlined"
            size="small"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
            sx={{ textTransform: "none" }}
          >
            Go to Pay
          </Button>
        );
      },
    },
    {
      id: "invoice_file_url",
      label: "Invoice File",
      minWidth: 150,
      format: (value, row) => {
        if (!value) return "-";
        return (
          <IconButton
            size="small"
            onClick={() => window.open(value, "_blank", "noopener,noreferrer")}
            title="Open Invoice File"
            color="primary"
          >
            <AttachFileIcon />
          </IconButton>
        );
      },
    },
    {
      id: "created_date",
      label: withTz("Created Date"),
      minWidth: 150,
      format: (value) => formatUtcToLocal(value) || "-",
    },
    {
      id: "transaction_updated_at",
      label: withTz("Updated At"),
      minWidth: 150,
      format: (value) => formatUtcToLocal(value) || "-",
    },
    {
      id: "action",
      label: "Actions",
      actions: [
        (row) => {
          return row.status === "P" ? (
            <IconButton title={`Edit Invoice`} onClick={() => handleEdit(row)}>
              <EditIcon />
            </IconButton>
          ) : null;
        },
        (row) => {
          return row.status === "P" ? (
            <IconButton
              title={`Cancel Invoice`}
              onClick={() => handleDelete(row)}
            >
              <DeleteIcon />
            </IconButton>
          ) : null;
        },
      ],
    },
  ];

  return (
    <InvoicesListRootArea>
      <GridContainer>
        <GridItem xs={12}>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1.5}
            alignItems="center"
            sx={{ mt: 2, mb: 1.5 }}
          >
            <TextField
              label="Search"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Invoice number or currency"
              sx={{ minWidth: { xs: "100%", sm: 240 }, flexGrow: 1 }}
            />
            <Button variant="contained" size="small" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="text" size="small" onClick={handleClearSearch}>
              Clear
            </Button>
          </Box>
        </GridItem>

        <GridItem xs={12}>
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Invoices</Typography>
            <Box display="flex" flexWrap="nowrap" gap={1} flexShrink={0}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleGoBack}
                size="small"
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAdd}
                size="small"
                startIcon={<AddIcon />}
              >
                Add Invoice
              </Button>
            </Box>
          </Box>
        </GridItem>

        <GridItem xs={12}>
          <DataTable
            loading={status === "loading"}
            columns={columns}
            rows={invoicesList}
            page={page - 1}
            rowsPerPage={pageSize}
            count={totalRecords}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            noDataText="No invoices available"
          />
        </GridItem>
      </GridContainer>
    </InvoicesListRootArea>
  );
};

export default Invoices;
